import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MessageSquare,
  Bug,
  Lightbulb,
  AlertTriangle,
  Trash2,
  RefreshCw,
  Filter,
  Image as ImageIcon,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ActionTile } from "@/components/ui/ActionTile";
import { TINT } from "@/components/ui/IconTile";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  new: { label: "New", color: "bg-blue-100 text-blue-700 border-blue-200" },
  in_progress: {
    label: "In Progress",
    color: "bg-amber-100 text-amber-800 border-amber-200",
  },
  done: { label: "Done", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  wont_fix: { label: "Won't Fix", color: "bg-slate-200 text-slate-700 border-slate-300" },
};

const TYPE_META = {
  bug: { label: "Bug", icon: Bug, color: "text-red-600 bg-red-50" },
  feature: { label: "Feature", icon: Lightbulb, color: "text-amber-600 bg-amber-50" },
  suggestion: {
    label: "Suggestion",
    icon: MessageSquare,
    color: "text-blue-600 bg-blue-50",
  },
  other: { label: "Other", icon: AlertTriangle, color: "text-slate-600 bg-slate-50" },
};

const PRIORITY_META = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-orange-100 text-orange-800",
  high: "bg-red-100 text-red-700",
};

const FeedbackManager = ({ getAuthHeader, onAuthFailure }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [screenshotFor, setScreenshotFor] = useState(null); // feedback id
  const [screenshotUrl, setScreenshotUrl] = useState("");

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/admin/feedback${statusFilter !== "all" ? `?status=${statusFilter}` : ""}`,
        getAuthHeader()
      );
      setItems(r.data.items || []);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        onAuthFailure?.();
      } else {
        toast.error("Failed to load feedback");
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, getAuthHeader, onAuthFailure]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  const changeStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/feedback/${id}`,
        { status },
        getAuthHeader()
      );
      toast.success("Status updated");
      fetchFeedback();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this feedback permanently?")) return;
    try {
      await axios.delete(`${API}/admin/feedback/${id}`, getAuthHeader());
      toast.success("Deleted");
      fetchFeedback();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const viewScreenshot = async (id) => {
    // Use the current admin bearer to fetch the image, then display via blob URL.
    try {
      const authHeader = getAuthHeader().headers;
      const r = await fetch(`${API}/feedback/screenshot/${id}`, {
        headers: authHeader,
        // Also send the httpOnly admin cookie so the fetch works right
        // after a page reload — before the axios refresh cycle has
        // re-hydrated the in-memory bearer.
        credentials: "include",
      });
      if (!r.ok) throw new Error("Failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      setScreenshotUrl(url);
      setScreenshotFor(id);
    } catch (e) {
      toast.error("Could not load screenshot");
    }
  };

  const closeScreenshot = () => {
    if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
    setScreenshotFor(null);
    setScreenshotUrl("");
  };

  return (
    <div className="space-y-4" data-testid="feedback-manager">
      <Card className="bg-white border border-gray-100 shadow-lg rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
          <CardTitle className="flex items-center gap-2 text-2xl">
            <MessageSquare className="w-6 h-6 text-cyan-600" />
            User Feedback &amp; Feature Requests
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-slate-500 text-sm">
              <Filter className="w-4 h-4" />
              Status
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="feedback-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="done">Done</SelectItem>
                <SelectItem value="wont_fix">Won't Fix</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchFeedback}
              disabled={loading}
              data-testid="feedback-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading && items.length === 0 ? (
            <p className="text-center text-slate-500 py-12">Loading…</p>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <MessageSquare className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <p className="font-medium">No feedback yet</p>
              <p className="text-sm mt-1">
                Submissions from account owners will show up here.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((it) => {
                const TypeIcon = TYPE_META[it.type]?.icon || MessageSquare;
                return (
                  <div
                    key={it.id}
                    className="border border-slate-200 rounded-xl p-4 hover:shadow-sm transition-shadow"
                    data-testid={`feedback-row-${it.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                              TYPE_META[it.type]?.color || TYPE_META.other.color
                            }`}
                          >
                            <TypeIcon className="w-3 h-3" />
                            {TYPE_META[it.type]?.label || it.type}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                              PRIORITY_META[it.priority] || PRIORITY_META.low
                            }`}
                          >
                            {it.priority?.toUpperCase()}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${
                              STATUS_META[it.status]?.color ||
                              "bg-slate-100 text-slate-700 border-slate-200"
                            }`}
                          >
                            {STATUS_META[it.status]?.label || it.status}
                          </span>
                          <span className="text-xs text-slate-400 ml-auto">
                            {it.created_at ? new Date(it.created_at).toLocaleString() : ""}
                          </span>
                        </div>
                        <h3 className="font-semibold text-slate-800">{it.title}</h3>
                        <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">
                          {it.description}
                        </p>
                        <p className="text-xs text-slate-500 mt-2">
                          From:{" "}
                          <span className="font-medium">
                            {it.user_name || it.user_email || it.user_id || "unknown"}
                          </span>
                          {it.user_email && it.user_name && (
                            <span className="text-slate-400"> · {it.user_email}</span>
                          )}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <Select
                          value={it.status}
                          onValueChange={(v) => changeStatus(it.id, v)}
                        >
                          <SelectTrigger
                            className="w-40"
                            data-testid={`feedback-status-${it.id}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="done">Done</SelectItem>
                            <SelectItem value="wont_fix">Won't Fix</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="flex items-center gap-2">
                          {it.screenshot_id && (
                            <button
                              type="button"
                              onClick={() => viewScreenshot(it.id)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-lg bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 transition-colors"
                              data-testid={`feedback-screenshot-${it.id}`}
                            >
                              <ImageIcon className="w-3.5 h-3.5" />
                              Screenshot
                            </button>
                          )}
                          <ActionTile
                            icon={Trash2}
                            tint={TINT.expenses}
                            onClick={() => deleteItem(it.id)}
                            title="Delete"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {screenshotFor && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={closeScreenshot}
          data-testid="feedback-screenshot-modal"
        >
          <img
            src={screenshotUrl}
            alt="Feedback screenshot"
            className="max-w-full max-h-full rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default FeedbackManager;
